import { useRef, useState } from "react"
import styled, { css, useTheme } from "styled-components"
// This component contains a custom checkbox version of select multiple
import type { SelectMultipleOption } from "~/ur-react-components"
import { useClickOutside, useTranslate } from "~/ur-react-hooks"
import { CheckBox } from "../Checkbox"
import { Icon } from "../Icon"

interface LayoutProps {
  isOpen: boolean
  width?: string
}

const Layout = styled.div<LayoutProps>`
  position: relative;
  height: 48px;

  width: ${(props) => props.width};
  border-radius: 4px;
  box-sizing: border-box;
`

interface DisplayProps {
  isOpen: boolean
  width?: string
  hasError: boolean
  placeholderColor?: string
  isPlaceholderDisplayed: boolean
}

const Display = styled.div<DisplayProps>`
  display: flex;
  height: 48px;
  align-items: center;

  padding: 12px;
  box-sizing: border-box;

  border-radius: 4px;
  color: ${(props) => props.theme.colors.gray0};
  width: ${(props) => props.width};

  border: 1px solid ${(props) => props.theme.colors.gray70};

  ${(props) =>
    props.isPlaceholderDisplayed &&
    props.placeholderColor &&
    css`
      color: ${props.placeholderColor};
    `}

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};
      padding-left: 11px;
      padding-bottom: 14px;

      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
      padding-left: 11px;
      padding-bottom: 14px;
    `}
`

const ChevronWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 1rem;
`

interface OptionsProps {
  isOpen: boolean
  width?: string
  hasError: boolean
}

const Options = styled.ul<OptionsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};

  position: absolute;
  z-index: 4000;
  top: 46px;

  margin: 0;
  padding: 0;
  width: ${(props) => props.width};
  box-sizing: border-box;

  box-shadow: ${(props) => props.theme.shadow.primary};

  list-style: none;
  background: white;
  border-radius: 0 0 4px 4px;

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};

      border-top-right-radius: 0;
      border-top-left-radius: 0;
      border-top: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
      border-top: none;
    `}
`

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  box-sizing: border-box;
  gap: 1rem;

  height: 48px;
  color: ${(props) => props.theme.colors.gray30};

  &:hover {
    background: ${(props) => props.theme.colors.primary95};
  }
`

const DisplayOption = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  padding-left: 1px;
  box-sizing: border-box;
  gap: 1rem;
  width: 100%;

  height: 48px;
  color: ${(props) => props.theme.colors.gray30};
`

const Option = styled.li`
  ${OptionLabel}:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`

const Error = styled.div`
  color: ${(props) => props.theme.colors.error40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
`

const Warning = styled.div`
  color: ${(props) => props.theme.colors.warning40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;

  position: absolute;
`

export interface CheckSelectMultipleProps<ValueType extends string | number> {
  value: ValueType[]
  onChange: (newValues: ValueType[]) => void
  options: SelectMultipleOption<ValueType>[]

  width?: string
  placeholder?: string
  placeholderColor?: string

  error?: string | null
  warning?: string | null
}

const CheckSelectMultiple = <ValueType extends string | number>({
  value,
  onChange,
  options,
  width,
  error,
  warning,
  placeholder: _placeholder,
  placeholderColor,
}: CheckSelectMultipleProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)
  const theme = useTheme()

  const translations = useTranslate({
    multiple: "select-multiple.multiple-selected",
    defaultPlaceholder: "common.select-placeholder",
    selectAll: "select-multiple.select-all",
    removeAll: "select-multiple.deselect-all",
  })

  useClickOutside(ref, () => {
    setIsOpen(false)
  })

  const placeholder = _placeholder || translations.defaultPlaceholder
  const selectedOptions = options.filter((option) =>
    value.includes(option.value),
  )
  const valueDisplay =
    selectedOptions.length > 1
      ? translations.multiple
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : placeholder

  const isPlaceholderDisplayed = selectedOptions.length === 0

  const allSelected = options.length === selectedOptions.length

  return (
    <Layout ref={ref} isOpen={isOpen} width={width}>
      <Display
        isOpen={isOpen}
        width={width}
        onClick={() => setIsOpen(true)}
        hasError={!!error}
        isPlaceholderDisplayed={isPlaceholderDisplayed}
        placeholderColor={placeholderColor}
      >
        {!isOpen && (
          <>
            {valueDisplay}
            <ChevronWrapper>
              <Icon
                icon="chevron-down"
                size="16px"
                color={theme.colors.gray50}
              />
            </ChevronWrapper>
          </>
        )}
        {isOpen && (
          <DisplayOption
            onClick={() => {
              if (selectedOptions.length === 0) {
                onChange(options.map((option) => option.value))
              } else {
                onChange([])
              }
            }}
          >
            <CheckBox
              checked={selectedOptions.length > 0}
              // Handle this above
              onChange={() => void 0}
              checkedIconProps={{
                icon: allSelected ? "check" : "dash",
                color: "white",
                type: "solid",
              }}
              boxSize="18px"
              boxBorderRadius="4px"
            />
            {selectedOptions.length === 0
              ? translations.selectAll
              : translations.removeAll}
            <ChevronWrapper
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(false)
              }}
            >
              <Icon icon="chevron-up" size="16px" color={theme.colors.gray50} />
            </ChevronWrapper>
          </DisplayOption>
        )}
      </Display>
      {error && <Error>{error}</Error>}
      {warning && <Warning>{warning}</Warning>}
      <Options isOpen={isOpen} width={width} hasError={!!error}>
        {options.map((option) => (
          <Option key={option.value}>
            <OptionLabel
              onClick={() => {
                const checked = !value.includes(option.value)
                if (checked) {
                  onChange([...value, option.value])
                } else {
                  onChange(value.filter((v) => v !== option.value))
                }
              }}
            >
              <CheckBox
                checked={value.includes(option.value)}
                onChange={(checked) => {
                  if (checked) {
                    onChange([...value, option.value])
                  } else {
                    onChange(value.filter((v) => v !== option.value))
                  }
                }}
                checkedIconProps={{ color: "white", type: "solid" }}
                boxSize="18px"
                boxBorderRadius="4px"
              />
              {option.label}
            </OptionLabel>
          </Option>
        ))}
      </Options>
    </Layout>
  )
}

export default CheckSelectMultiple
